import { Text } from "@sfc/ui";
import { cn } from "@client/utils/cn";
import { type RoundVisitRow } from "@lib/todoRailTypes";
import {
  BuildingIcon,
  ClockIcon,
  CompassIcon,
  LayersIcon,
  ScaleIcon,
  UserIcon,
  VideoIcon,
} from "lucide-react";
import { type ComponentType } from "react";

const kindIcons: Record<
  RoundVisitRow["kind"],
  ComponentType<{ className?: string }>
> = {
  video: VideoIcon,
  recommender: UserIcon,
  proposal: BuildingIcon,
  decide: ScaleIcon,
  overview: CompassIcon,
  track: LayersIcon,
};

export const visitKindLabels: Record<RoundVisitRow["kind"], string> = {
  video: "Video",
  recommender: "Recommender",
  proposal: "Proposal",
  decide: "Decide",
  overview: "Overview",
  track: "Track",
};

/**
 * "How long ago", at the resolution a reader actually cares about.
 *
 * Deliberately coarse: whether you last opened a recommender 14 or 17 minutes
 * ago changes nothing, and a ticking exact figure invites re-reading a number
 * that carries no decision.
 */
function relativeTime(iso: string, now: number): string {
  const seconds = Math.max(0, (now - new Date(iso).getTime()) / 1000);

  if (seconds < 90) {
    return "just now";
  }

  const minutes = Math.round(seconds / 60);

  if (minutes < 60) {
    return `${minutes} min ago`;
  }

  const hours = Math.round(minutes / 60);

  if (hours < 24) {
    return `${hours}h ago`;
  }

  const days = Math.round(hours / 24);
  return days === 1 ? "yesterday" : `${days}d ago`;
}

/**
 * Whether a History row can be opened — and, when it can't, WHY.
 *
 * "No longer available" is a claim that the thing is gone, so it needs a read
 * that happened: a failed or pending video catalog resolved every video row to
 * "unaddressable", which the list then reported as deleted and the Resume card
 * skipped past as though it were. Unknown is not gone.
 */
export type HistoryTarget =
  | { state: "ready"; href: string }
  | { state: "unknown" }
  | { state: "gone" };

export function TodoHistoryList({
  visits,
  isLoading,
  now,
  resolveTarget,
  onOpen,
}: {
  /** Null until the history has actually been read — see the empty state. */
  visits: RoundVisitRow[] | null;
  isLoading: boolean;
  /** Passed in so the list is a pure render — see the test for `relativeTime`. */
  now: number;
  /** Rebuilds the link from kind + id, or says why it can't. */
  resolveTarget: (visit: RoundVisitRow) => HistoryTarget;
  onOpen: (visit: RoundVisitRow, href: string) => void;
}) {
  // "Nowhere yet" is a CLAIM about this funder's round, so it may only be made
  // once the history has been read. A failed or in-flight fetch produced it
  // from an empty default — telling someone with a round's worth of history
  // that they had none, and hiding the resume card with it. Same rule the To Do
  // tab applies to its own reads.
  if (visits == null) {
    return (
      <Text as="p" variant="body-sm" tone="muted" className="px-6">
        {isLoading ? "Loading your history…" : "Couldn’t load your history."}
      </Text>
    );
  }

  if (visits.length === 0) {
    return (
      <Text as="p" variant="body-sm" tone="muted" className="px-6">
        Nowhere yet — the places you open in this round show up here.
      </Text>
    );
  }

  return (
    <ul className="flex flex-col gap-0.5 px-6">
      {visits.map((visit) => {
        const Icon = kindIcons[visit.kind] ?? ClockIcon;
        const target = resolveTarget(visit);
        const href = target.state === "ready" ? target.href : null;
        return (
          <li key={visit.targetKey}>
            <button
              type="button"
              disabled={href == null}
              onClick={() => href != null && onOpen(visit, href)}
              className={cn(
                "flex w-full items-center gap-3 rounded-lg border border-transparent px-2 py-2 text-left",
                href != null
                  ? "hover:border-border-subtle hover:bg-surface-elevated"
                  : "cursor-default opacity-60",
              )}
            >
              <span className="grid size-7 flex-none place-items-center rounded-md bg-surface-elevated text-text-muted">
                <Icon className="size-3.5" />
              </span>
              <span className="flex min-w-0 flex-1 flex-col">
                <Text
                  as="span"
                  variant="body-sm"
                  className="leading-snug font-medium break-words"
                >
                  {visit.label}
                </Text>
                <Text as="span" variant="body-sm" tone="muted">
                  {visitKindLabels[visit.kind] ?? "Page"}
                  {target.state === "gone" && " · no longer available"}
                </Text>
              </span>
              <Text
                as="span"
                variant="label-sm"
                tone="muted"
                className="flex-none tabular-nums"
              >
                {relativeTime(visit.visitedAt, now)}
              </Text>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export { relativeTime };
